import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Modal, Stack, Group, Text, Badge, Card, Button, MultiSelect, Divider, Table } from '@mantine/core';
import { IconX, IconPencil } from '@tabler/icons-react';
import useMembersManager from '../../hooks/useMembersManager';
import { db } from '../../firebase';
import { useTranslation } from 'react-i18next';

export default function OptimizationResultsModal({
  opened,
  onClose,
  planningId,
  run,
  result,
  assignableUsers = [],
  events = [],
  onApply,
  onCancel,
}) {
  const { t } = useTranslation('common');
  const idx = React.useMemo(() => run?.optimization_file?.optimizationIndexes || {}, [run]);
  const usersMap = React.useMemo(() => idx.users || {}, [idx]); // U# -> uid
  const eventsMap = React.useMemo(() => idx.events || {}, [idx]); // E# -> eventId
  const rolesMap = React.useMemo(() => idx.roles || {}, [idx]); // R# -> roleId/name
  const eligibleRolesByUser = React.useMemo(() => idx.eligibleRolesByUser || {}, [idx]); // U# -> [R#]
  const staffRequired = React.useMemo(() => idx.staffRequired || {}, [idx]); // E# -> number
  const staffRequiredByRole = React.useMemo(() => idx.staffRequiredByRole || {}, [idx]); // E# -> { R#: count }
  const userUnavailable = React.useMemo(() => idx.userUnavailable || {}, [idx]); // U# -> [E#]

  // Merge live members (users + ghosts) so names are available even if parent list is stale
  const { participants: liveMembers } = useMembersManager({ db, planningId, currentUserId: null });
  const liveOptions = useMemo(() => (Array.isArray(liveMembers) ? liveMembers.map((p) => ({ value: p.uid, label: p.displayName || p.email || p.uid })) : []), [liveMembers]);
  const mergedOptions = useMemo(() => {
    const map = new Map();
    for (const u of assignableUsers || []) map.set(u.value, u.label);
    for (const u of liveOptions || []) map.set(u.value, u.label);
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [assignableUsers, liveOptions]);
  const nameByUid = useMemo(() => {
    const m = new Map();
    for (const o of mergedOptions) m.set(o.value, o.label);
    return m;
  }, [mergedOptions]);

  // Normalize solver variables into a map: { varName -> value }
  const vars = useMemo(() => {
    const r = result || {};
    let v = r.vars || r.Vars || r.variables || r?.solution?.vars || r?.solution?.variables || r?.result?.variables || {};
    // If array like [{ id/name, value }], convert to object
    if (Array.isArray(v)) {
      const out = {};
      for (const item of v) {
        if (!item) continue;
        const key = item.id || item.name || item.var || item.variable;
        const val = item.value ?? item.val ?? item.x ?? item.v;
        if (key != null) out[String(key)] = val;
      }
      return out;
    }
    // If nested like { values: [...] }
    if (v && Array.isArray(v.values)) {
      const out = {};
      for (const item of v.values) {
        if (!item) continue;
        const key = item.id || item.name || item.var || item.variable;
        const val = item.value ?? item.val ?? item.x ?? item.v;
        if (key != null) out[String(key)] = val;
      }
      return out;
    }
    // If it's already an object map
    return (typeof v === 'object' && v) ? v : {};
  }, [result]);

  const eventById = useMemo(() => {
    const map = new Map();
    for (const e of Array.isArray(events) ? events : []) {
      map.set(e.id, e);
    }
    return map;
  }, [events]);

  // Reverse helper maps
  const userKeyByUid = useMemo(() => Object.entries(usersMap).reduce((acc, [uk, uid]) => { acc[uid] = uk; return acc; }, {}), [usersMap]);
  const roleKeyByName = useMemo(() => Object.entries(rolesMap).reduce((acc, [rk, name]) => { acc[name] = rk; return acc; }, {}), [rolesMap]);

  // Helpers to fetch required roles for an event id
  const eventKeyById = useMemo(() => Object.entries(eventsMap).reduce((acc, [ek, eid]) => { acc[eid] = ek; return acc; }, {}), [eventsMap]);
  const rolesRequiredForEid = useCallback((eid) => {
    const eKey = eventKeyById[eid];
    const rr = staffRequiredByRole[eKey] || {};
    const entries = Object.entries(rr);
    if (!entries.length) {
      // Fallback to single default role using staffRequired
      const total = Number(staffRequired[eKey] || 0);
      if (!total) return [];
      return [{ roleId: 'default', rKey: 'R1', count: total }];
    }
    return entries.map(([rKey, count]) => ({ roleId: rolesMap[rKey] || rKey, rKey, count: Number(count) || 0 }));
  }, [eventKeyById, staffRequiredByRole, staffRequired, rolesMap]);

  // Build initial assignments from solver variables (per role)
  const initialByEvent = useMemo(() => {
    const mapping = {};
    for (const [name, value] of Object.entries(vars)) {
      const m = /^x_(U\d+)_(E\d+)_(R\d+)$/.exec(name);
      const mLegacy = !m ? /^x_(U\d+)_(E\d+)$/.exec(name) : null;
      if (!m && !mLegacy) continue;
      const on = Number(value) >= 1 || value === true;
      if (!on) continue;
      const uKey = (m || mLegacy)[1];
      const eKey = (m || mLegacy)[2];
      const rKey = m ? m[3] : null;
      const uid = usersMap[uKey];
      const eid = eventsMap[eKey];
      if (!uid || !eid) continue;
      const roleId = rKey ? (rolesMap[rKey] || rKey) : 'default';
      if (!mapping[eid]) mapping[eid] = {};
      if (!mapping[eid][roleId]) mapping[eid][roleId] = [];
      if (!mapping[eid][roleId].includes(uid)) mapping[eid][roleId].push(uid);
    }
    return mapping;
  }, [vars, usersMap, eventsMap, rolesMap]);

  const [byEventSelection, setByEventSelection] = useState(initialByEvent);
  const [editMode, setEditMode] = useState({}); // eid -> boolean
  const [refused, setRefused] = useState({}); // eid -> boolean

  useEffect(() => {
    setByEventSelection(initialByEvent);
    setEditMode({});
    setRefused({});
  }, [opened, initialByEvent]);

  const inScopeEventIds = useMemo(() => Object.values(eventsMap), [eventsMap]);

  // Analytics
  const analytics = useMemo(() => {
    const totalEvents = inScopeEventIds.length;
    let fullyAssigned = 0;
    let partiallyAssigned = 0;
    let fullyUnassigned = 0;
    const breachEventIds = new Set();
    const userKeyByUid = Object.entries(usersMap).reduce((acc, [uk, uid]) => { acc[uid] = uk; return acc; }, {});
    for (const eid of inScopeEventIds) {
      const roles = rolesRequiredForEid(eid);
      const srTotal = roles.reduce((s, r) => s + (r.count || 0), 0) || Number(staffRequired[eventKeyById[eid]] || 0) || 0;
      const assignedTotal = Object.values(initialByEvent[eid] || {}).reduce((s, arr) => s + (Array.isArray(arr) ? arr.length : 0), 0);
      if (srTotal > 0 && assignedTotal === srTotal) fullyAssigned += 1;
      else if (assignedTotal > 0 && assignedTotal < srTotal) partiallyAssigned += 1;
      else if (srTotal > 0 && assignedTotal === 0) fullyUnassigned += 1;
      // Breach: any assigned user that is unavailable for this event key
      const eKey = eventKeyById[eid];
      for (const uids of Object.values(initialByEvent[eid] || {})) {
        for (const uid of (uids || [])) {
          const uKey = userKeyByUid[uid];
          if (!uKey || !eKey) continue;
          const unavail = userUnavailable[uKey] || [];
          if (unavail.includes(eKey)) breachEventIds.add(eid);
        }
      }
    }
    return { totalEvents, fullyAssigned, partiallyAssigned, fullyUnassigned, availabilityBreaches: breachEventIds.size };
  }, [inScopeEventIds, initialByEvent, usersMap, staffRequired, userUnavailable, rolesRequiredForEid, eventKeyById]);

  const assignedEventIds = useMemo(() => Object.keys(initialByEvent).filter((eid) => {
    const v = initialByEvent[eid];
    if (!v || typeof v !== 'object') return false;
    return Object.values(v).some((arr) => Array.isArray(arr) && arr.length > 0);
  }), [initialByEvent]);

  const getStaffRequiredForEid = (eid) => {
    // Total across roles
    const roles = rolesRequiredForEid(eid);
    const total = roles.reduce((s, r) => s + (r.count || 0), 0);
    if (total) return total;
    const eKey = Object.entries(eventsMap).find(([k, v]) => v === eid)?.[0];
    return Number(staffRequired[eKey] || 1);
  };

  const handleRefuse = (eid) => {
    setRefused((prev) => ({ ...prev, [eid]: true }));
    setEditMode((prev) => ({ ...prev, [eid]: false }));
    // Keep selection but mark refused; apply step will ignore refused events
  };

  // --- Per-member stats over the in-scope period (based on current selection) ---
  const periodInfo = useMemo(() => {
    // Prefer exact optimization time scope when available to avoid off-by-one from event end times
    const scope = run?.optimization_file?.timeScope;
    const msPerDay = 24 * 60 * 60 * 1000;
    if (scope && scope.startDate && scope.endDate) {
      // Parse YYYY-MM-DD as local dates at midnight
      const [ys, ms, ds] = String(scope.startDate).split('-').map((n) => Number(n));
      const [ye, me, de] = String(scope.endDate).split('-').map((n) => Number(n));
      const startYmd = new Date(ys || 0, (ms || 1) - 1, ds || 1);
      const endYmd = new Date(ye || 0, (me || 1) - 1, de || 1);
      startYmd.setHours(0, 0, 0, 0);
      endYmd.setHours(0, 0, 0, 0);
      const totalDays = Math.max(1, Math.round((endYmd - startYmd) / msPerDay) + 1);
      const weeksRatio = totalDays / 7;
      return { start: startYmd, end: endYmd, totalDays, weeksRatio };
    }

    // Fallback: infer from in-scope events (may slightly overcount if an event ends exactly at midnight next day)
    let minStart = null;
    let maxEnd = null;
    for (const eid of inScopeEventIds) {
      const ev = eventById.get(eid);
      if (!ev) continue;
      const s = ev.start ? new Date(ev.start) : null;
      const e = ev.end ? new Date(ev.end) : null;
      if (s && (!minStart || s < minStart)) minStart = s;
      if (e && (!maxEnd || e > maxEnd)) maxEnd = e;
    }
    if (!minStart || !maxEnd) return { start: null, end: null, totalDays: 0, weeksRatio: 0 };
    const startYmd = new Date(minStart.getFullYear(), minStart.getMonth(), minStart.getDate());
    const endYmd = new Date(maxEnd.getFullYear(), maxEnd.getMonth(), maxEnd.getDate());
    const totalDays = Math.max(1, Math.round((endYmd - startYmd) / msPerDay) + 1);
    const weeksRatio = totalDays / 7;
    return { start: minStart, end: maxEnd, totalDays, weeksRatio };
  }, [inScopeEventIds, eventById, run]);

  const perUserStats = useMemo(() => {
    const stats = new Map(); // uid -> { name, assignments:Set, perDay:{ key -> {min,max,mins} } }
    const get = (uid) => {
      if (!stats.has(uid)) stats.set(uid, { uid, name: nameByUid.get(uid) || uid, assignments: new Set(), perDay: new Map(), totalMins: 0 });
      return stats.get(uid);
    };
    const dayKey = (d) => {
      const dt = new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
    };
    const eventMinutes = (ev) => {
      const eff = Number(ev?.extendedProps?.effectiveMinutes);
      if (Number.isFinite(eff) && eff > 0) return eff;
      const s = ev?.start ? new Date(ev.start) : null;
      const e = ev?.end ? new Date(ev.end) : null;
      if (!s || !e) return 0;
      return Math.max(0, Math.round((e - s) / 60000));
    };
    // Build from current selection to reflect edits
    for (const eid of inScopeEventIds) {
      const ev = eventById.get(eid);
      if (!ev) continue;
      const selections = byEventSelection[eid] || {};
      // Deduplicate per user per event
      const seenByUser = new Set();
      const mins = eventMinutes(ev);
      let s = ev.start ? new Date(ev.start) : null;
      let e = ev.end ? new Date(ev.end) : null;
      const dk = s ? dayKey(s) : null;
      for (const arr of Object.values(selections)) {
        const users = Array.isArray(arr) ? arr : [];
        for (const uid of users) {
          const key = `${uid}::${eid}`;
          if (seenByUser.has(key)) continue; // Avoid double counting if assigned to multiple roles
          seenByUser.add(key);
          const st = get(uid);
          st.assignments.add(eid);
          if (dk) {
            const cur = st.perDay.get(dk) || { min: s, max: e, mins: 0 };
            // Update amplitude boundaries
            if (s && (!cur.min || s < cur.min)) cur.min = s;
            if (e && (!cur.max || e > cur.max)) cur.max = e;
            cur.mins += mins;
            st.perDay.set(dk, cur);
          }
          st.totalMins += mins;
        }
      }
    }
    // Ensure we include all users in optimization scope even if 0 assignments
    for (const uid of Object.values(usersMap)) {
      get(uid);
    }
    // Finalize computed metrics
    const rows = Array.from(stats.values()).map((st) => {
      const daysWorked = Array.from(st.perDay.values()).filter((d) => (d?.mins || 0) > 0).length;
      const totalHours = st.totalMins / 60;
      const avgHoursPerWorkedDay = daysWorked > 0 ? totalHours / daysWorked : 0;
      let maxAmplitudeHours = 0;
      for (const d of st.perDay.values()) {
        if (d.min && d.max && d.max > d.min) {
          const h = (d.max - d.min) / 3600000;
          if (h > maxAmplitudeHours) maxAmplitudeHours = h;
        }
      }
      const weeks = periodInfo.weeksRatio || 0;
      const avgWeeklyHours = weeks > 0 ? (totalHours / weeks) : 0;
      return {
        uid: st.uid,
        name: st.name,
        assignedEventsCount: st.assignments.size,
        avgHoursPerWorkedDay,
        maxAmplitudeHours,
        totalHours,
        avgWeeklyHours,
        daysWorked,
        totalDays: periodInfo.totalDays || 0,
      };
    });
    // Sort by total hours desc for readability
    rows.sort((a, b) => (b.totalHours - a.totalHours));
    return rows;
  }, [byEventSelection, inScopeEventIds, eventById, usersMap, nameByUid, periodInfo]);

  const toggleEdit = (eid) => setEditMode((prev) => ({ ...prev, [eid]: !prev[eid] }));

  const displayedCards = assignedEventIds.map((eid) => {
    const ev = eventById.get(eid);
    const roles = rolesRequiredForEid(eid);
    const selections = byEventSelection[eid] || {};
    const srTotal = getStaffRequiredForEid(eid);
    const summary = roles.map(({ roleId }) => {
      const users = selections[roleId] || [];
      const names = users.map((uid) => nameByUid.get(uid) || uid).join(', ');
      return `${roleId}: ${names || '—'}`;
    }).join('  •  ');
    return (
      <Card key={eid} withBorder>
        <Group justify="space-between" align="flex-start">
          <Stack gap={6} style={{ flex: 1 }}>
            <Text fw={600}>{ev?.title || 'Event'}</Text>
            {!editMode[eid] ? (
              <Text size="sm" c="dimmed">{summary || 'Unassigned'}</Text>
            ) : (
              <Stack gap={8}>
                {roles.map(({ roleId, count }) => {
                  // Filter choices to eligible users for this role when possible
                  const rk = roleKeyByName[roleId] || null;
                  const eligibleSet = new Set(
                    Object.entries(userKeyByUid)
                      .filter(([uid, uKey]) => (rk ? (eligibleRolesByUser[uKey] || []).includes(rk) : true))
                      .map(([uid]) => uid)
                  );
                  const options = mergedOptions.filter((u) => eligibleSet.has(u.value));
                  const value = selections[roleId] || [];
                  return (
                    <div key={roleId}>
                      <Text size="sm" fw={600} mb={4}>{roleId} <Badge variant="light" ml={6}>{t('opt_results.required')}: {count}</Badge></Text>
                      <MultiSelect
                        data={options}
                        value={value}
                        onChange={(vals) => {
                          const limited = Array.isArray(vals) ? vals.slice(0, count) : [];
                          setByEventSelection((prev) => ({
                            ...prev,
                            [eid]: { ...prev[eid], [roleId]: limited },
                          }));
                        }}
                        searchable
                        clearable
                        placeholder={`${t('opt_results.select')} ${roleId}`}
                      />
                      <Group gap={6} wrap="wrap" mt={6}>
                        {(value || []).map((uid) => (
                          <Badge key={uid} variant="light">
                            {nameByUid.get(uid) || uid}
                          </Badge>
                        ))}
                      </Group>
                    </div>
                  );
                })}
              </Stack>
            )}
            <Badge variant="light">{t('opt_results.total_required')}: {srTotal}</Badge>
          </Stack>
          <Stack gap="xs" align="flex-end">
            <Button size="xs" variant="outline" color="red" leftSection={<IconX size={14} />} onClick={() => handleRefuse(eid)}>
              {t('opt_results.refuse')}
            </Button>
            <Button size="xs" variant="subtle" leftSection={<IconPencil size={14} />} onClick={() => toggleEdit(eid)}>
              {editMode[eid] ? t('common.done') : t('common.edit')}
            </Button>
            {refused[eid] && <Badge color="red" variant="light">{t('opt_results.refused')}</Badge>}
          </Stack>
        </Group>
      </Card>
    );
  });

  const applyAll = async () => {
    const byEvent = {};
    for (const [eid, rolesSel] of Object.entries(byEventSelection)) {
      if (refused[eid]) continue;
      const roles = rolesRequiredForEid(eid);
      const outRoles = {};
      for (const { roleId, count } of roles) {
        const arr = Array.isArray(rolesSel?.[roleId]) ? rolesSel[roleId].slice(0, count) : [];
        if (arr.length > 0) outRoles[roleId] = arr;
      }
      if (Object.keys(outRoles).length > 0) byEvent[eid] = outRoles;
    }
    await onApply?.(byEvent);
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('opt_results.title')} size="xl">
      <Stack gap="md">
        <Group>
          <Badge variant="light">{t('opt_results.total_events')}: {analytics.totalEvents}</Badge>
          <Badge color={analytics.partiallyAssigned ? 'yellow' : 'gray'} variant="light">{t('opt_results.partially_assigned')}: {analytics.partiallyAssigned}</Badge>
          <Badge color={analytics.fullyAssigned ? 'green' : 'gray'} variant="light">{t('opt_results.fully_assigned')}: {analytics.fullyAssigned}</Badge>
          <Badge color={analytics.fullyUnassigned ? 'gray' : 'gray'} variant="light">{t('opt_results.fully_unassigned')}: {analytics.fullyUnassigned}</Badge>
          <Badge color={analytics.availabilityBreaches ? 'red' : 'gray'} variant="light">{t('opt_results.availability_breaches')}: {analytics.availabilityBreaches}</Badge>
        </Group>
        <Divider my={4} />
        <div>
          <Text fw={600} mb={6}>{t('opt_results.member_workload')}</Text>
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('opt_results.col.member')}</Table.Th>
                <Table.Th>{t('opt_results.col.assigned_events')}</Table.Th>
                <Table.Th>{t('opt_results.col.avg_hours_per_day')}</Table.Th>
                <Table.Th>{t('opt_results.col.max_daily_amplitude')}</Table.Th>
                <Table.Th>{t('opt_results.col.total_hours')}</Table.Th>
                <Table.Th>{t('opt_results.col.avg_weekly_hours')}</Table.Th>
                <Table.Th>{t('opt_results.col.days_worked')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {perUserStats.map((r) => (
                <Table.Tr key={r.uid}>
                  <Table.Td>{r.name}</Table.Td>
                  <Table.Td>{r.assignedEventsCount}</Table.Td>
                  <Table.Td>{r.avgHoursPerWorkedDay.toFixed(1)}</Table.Td>
                  <Table.Td>{r.maxAmplitudeHours.toFixed(1)}</Table.Td>
                  <Table.Td>{r.totalHours.toFixed(1)}</Table.Td>
                  <Table.Td>{r.avgWeeklyHours.toFixed(1)}</Table.Td>
                  <Table.Td>{r.daysWorked}/{r.totalDays}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
        <Divider my={4} />
        <Stack gap="sm">
          {displayedCards.length ? displayedCards : (
            <Text size="sm" c="dimmed">{t('opt_results.no_assigned')}</Text>
          )}
        </Stack>
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={() => { onCancel?.(); }}>{t('common.cancel')}</Button>
          <Button onClick={applyAll}>{t('opt_results.apply_all')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
